import React, { useState, useEffect } from "react";
import { Save, Plus, Trash2 } from "lucide-react";
import { Button } from "../../components/Button";
import officeLocationApi from "../../api/officeLocationApi";
import FormField from "../../components/ui/FormField";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { indianStatesOnly } from "../../data/stateData";

const OfficeLocationsForm = ({
    businessInfoId,
    initialData = [],
    onSave,
    onRefreshAccounts,
    onCancel,
}) => {
    const token = useSelector((state) => state.auth.access_token);
    const navigate = useNavigate();

    const [locations, setLocations] = useState([]);
    const [errors, setErrors] = useState({});
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);


    const getEmptyLocation = () => ({
        id: null,
        address: "",
        city: "",
        state: "",
        pincode: "",
        locationType: "",
    });

    const isLocationComplete = (loc) =>
        loc.address?.trim() &&
        loc.city?.trim() &&
        loc.state?.trim() &&
        /^[0-9]{6}$/.test(loc.pincode) &&
        loc.locationType?.trim();


    useEffect(() => {
        if (initialData?.length > 0) {
            setLocations(initialData);
        } else {
            setLocations([getEmptyLocation()]);
        }
    }, [initialData]);


    const handleLocationChange = (index, field, value) => {
        const updated = [...locations];
        updated[index] = { ...updated[index], [field]: value };
        setLocations(updated);
        setHasUnsavedChanges(true);
    };

    const addLocation = () => {
        const last = locations[locations.length - 1];

        if (!isLocationComplete(last)) {
            toast.error("Please complete the current location first");
            return;
        }

        setLocations((prev) => [...prev, getEmptyLocation()]);
        setHasUnsavedChanges(true);
    };

    const removeLocation = (index) => {
        const updated = [...locations];
        updated.splice(index, 1);
        setLocations(updated);
        setHasUnsavedChanges(true);
    };


    const validateForm = () => {
        const newErrors = {};

        locations.forEach((loc, idx) => {
            if (!loc.city)
                newErrors[`city-${idx}`] = "City is required";

            if (!loc.state)
                newErrors[`state-${idx}`] = "State is required";

            if (!loc.pincode)
                newErrors[`pincode-${idx}`] = "Pincode is required";
            else if (!/^[0-9]{6}$/.test(loc.pincode))
                newErrors[`pincode-${idx}`] =
                    "Pincode must be exactly 6 digits";

            if (!loc.locationType)
                newErrors[`locationType-${idx}`] =
                    "Location Type is required";

            if (!loc.address)
                newErrors[`address-${idx}`] = "Address is required";
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleSubmit = async () => {
        if (!validateForm()) return;
if (!businessInfoId) {
        toast.error("Add business info first");
        return;
    }
        try {
            const updatedLocations = [];

            for (const loc of locations) {
                const payload = {
                    address: loc.address,
                    city: loc.city,
                    state: loc.state,
                    pincode: loc.pincode,
                    locationType: loc.locationType,
                };

                if (loc.id) {
                    await officeLocationApi.updateOfficeLocation(
                        loc.id,
                        payload,
                        token
                    );
                    updatedLocations.push(loc);
                }
                else {
                    const res =
                        await officeLocationApi.createOfficeLocation(
                            businessInfoId,
                            payload,
                            token
                        );

                    updatedLocations.push({
                        ...loc,
                        id: res?.data?.id || res?.id,
                    });
                }
            }

            setLocations(updatedLocations);
            setHasUnsavedChanges(false);
            onSave?.(updatedLocations);
onRefreshAccounts?.();
            toast.success("Office locations saved successfully");
            // navigate(-1);
        } catch (err) {
            console.error(err);
            toast.error("Failed to save office locations");
        }
    };

    const handleCancel = () => {
        if (hasUnsavedChanges) {
            if (
                window.confirm(
                    "You have unsaved changes. Are you sure you want to leave?"
                )
            ) {
                onCancel?.();
            }
        } else {
            onCancel?.();
        }
    };


    return (
        <div className="space-y-2">
            <div className="flex justify-between items-center mb-2">
                <h2 className="text-xl font-semibold text-gray-900">
                    Locations
                </h2>
                <Button
                    onClick={addLocation}
                    icon={<Plus className="h-4 w-4" />}
                    label="Add Location"
                />
            </div>

            {locations.map((location, index) => (
                <div
                    key={location.id ?? `temp-${index}`}
                    className="border border-gray-200 rounded-lg px-6 py-2 relative"
                >
                 {locations.length > 1 && !location.id && (
    <button
        type="button"
        onClick={() => removeLocation(index)}
        className="absolute top-4 right-4 text-red-500 hover:text-red-700"
        title="Remove Location"
    >
        <Trash2 size={16} />
    </button>
)}


                    <h4 className="font-medium mb-2">
                        Location #{index + 1}
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                        <FormField
                            label="City"
                            value={location.city}
                            onChange={(v) =>
                                handleLocationChange(index, "city", v)
                            }
                            error={errors[`city-${index}`]}
                        />

                        <FormField
                            label="State"
                            type="dropdown"
                            options={indianStatesOnly}
                            value={location.state}
                            onChange={(v) =>
                                handleLocationChange(index, "state", v)
                            }
                            error={errors[`state-${index}`]}
                        />

                        <FormField
                            label="Pincode"
                            value={location.pincode}
                            onChange={(v) =>
                                handleLocationChange(
                                    index,
                                    "pincode",
                                    v.replace(/\D/g, "").slice(0, 6)
                                )
                            }
                            error={errors[`pincode-${index}`]}
                        />

                        <FormField
                            label="Location Type"
                            type="dropdown"
                            options={[
                                "HEAD_OFFICE",
                                "BRANCH_OFFICE",
                                "REGISTERED_OFFICE",
                                "CORPORATE_OFFICE",
                                "SALES_OFFICE",
                                "Other",
                            ]}
                            value={location.locationType}
                            onChange={(v) =>
                                handleLocationChange(
                                    index,
                                    "locationType",
                                    v
                                )
                            }
                            error={errors[`locationType-${index}`]}
                        />
                    </div>

                    <FormField
                        label="Address"
                        type="description"
                        value={location.address}
                        onChange={(v) =>
                            handleLocationChange(index, "address", v)
                        }
                        error={errors[`address-${index}`]}
                    />
                </div>
            ))}

            <div className="flex justify-end space-x-3 mt-6">
                {/* <Button variant="outline" onClick={handleCancel} label="Cancel" /> */}
                <Button
                    onClick={handleSubmit}
                    icon={<Save className="h-4 w-4" />}
                    label="Save"
                />
            </div>
        </div>
    );
};

export default OfficeLocationsForm;
